import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, matchRoutes, useLocation } from 'react-router-dom';

import { ChevronRight as ChevronRightIcon } from 'components/icons';
import { useReducedMotion } from 'hooks/useReducedMotion';

import { NOT_FOUND_PATH } from 'routes/constants';
import MainRoutes from 'routes/MainRoutes';
import { useSelector } from 'store';
import { getBreadcrumbLeafKey } from 'utils/breadcrumbLeaf';
import { cn } from 'utils/cn';

// ==============================|| BREADCRUMBS ||============================== //

// Hand-rendered: the trail's structure is a synchronous
// function of `location` + route config, so crumbs appear/disappear the instant you
// navigate — before the destination route mounts. Only the leaf's *text* comes from
// the store, so its data-load lag delays text fill-in, not the animation.
//
// Every crumb (section or leaf) enters/exits via CSS (compositor), never JS/rAF: a
// rAF exit stutters while the main thread mounts a heavy destination page. The trail
// is append-only, so a navigation only adds or removes crumbs at the tail. Enter is a
// keyframe on mount; exit briefly retains the removed tail crumbs with `is-leaving`
// (a small presence list) so they can fade before unmounting. The new design, as reduced
// motion does, has none of it: the trail changes in one frame, and a leaving crumb goes at once.

type SectionCrumb = { path: string; title: string };
type BreadcrumbRoute = { breadcrumbs?: SectionCrumb[] };
type Crumb = { key: string; to?: string; labelId?: string; label?: string; mono?: boolean };

const EXIT_MS = 180;
// Max time to hold newly-added crumbs waiting for a leaf label before showing them anyway.
const LEAF_WAIT_MS = 900;

/**
 * The root crumb links to `/home`, which classic calls Dashboard. The new design calls it Home, shows it only on
 * that page itself, separates crumbs with a slash and draws them at body size.
 */
const Breadcrumbs = ({ newDesign = false }: { newDesign?: boolean }): React.ReactElement => {
    const location = useLocation();
    const intl = useIntl();
    const noMotion = useReducedMotion() || newDesign;
    const { leafLabel, leafKey, leafCrumb } = useSelector((state) => state.breadcrumb);

    // The trail the URL wants right now: the root + section crumbs (from route config)
    // + an optional leaf whose text comes from the store (gated by leafKey so a stale
    // label from a different entity is rejected, and deduped against the last section).
    // Also reports whether the route expects a leaf at all (`currentLeafKey`) and whether
    // its label has arrived (`leafReady`) — used to group the enter of deep navigations.
    const { structuralTarget, currentLeafKey, leafReady } = useMemo(() => {
        if (location.pathname === NOT_FOUND_PATH) {
            return { structuralTarget: [] as Crumb[], currentLeafKey: null as string | null, leafReady: false };
        }
        const leafMatch = matchRoutes([MainRoutes], location)?.at(-1);
        const sectionCrumbs = (leafMatch?.route as BreadcrumbRoute | undefined)?.breadcrumbs ?? [];
        const clk = leafMatch ? getBreadcrumbLeafKey(location.pathname, leafMatch.params) : null;
        const ready = !!(clk && leafKey === clk && leafLabel);

        let leaf = ready ? (leafCrumb ?? leafLabel) : null;
        const lastSection = sectionCrumbs.at(-1);
        if (leaf && lastSection && leaf === intl.formatMessage({ id: lastSection.title })) {
            leaf = null;
        }

        const crumbs: Crumb[] = [
            { key: 'dashboard', to: '/home', labelId: newDesign ? 'menu.home' : 'dashboard.dashboard' },
        ];
        sectionCrumbs.forEach((section) =>
            crumbs.push({ key: `section:${section.path}`, to: section.path, labelId: section.title })
        );
        if (leaf) {
            // The new design names a case by its number, in mono. `/matter/:matterId/…` pages carry no `id`. A case
            // with no number yet, a draft, is named by its name, in the page's font.
            const mono = newDesign && !!leafCrumb && !!leafMatch?.params.id && !!clk?.startsWith('matter#');
            crumbs.push({ key: `leaf:${leaf}`, label: leaf, mono });
        }
        if (newDesign && crumbs.length > 1) {
            crumbs.shift();
        }
        return { structuralTarget: crumbs, currentLeafKey: clk, leafReady: ready };
    }, [location, leafKey, leafLabel, leafCrumb, intl, newDesign]);

    // On a detail route whose leaf label is still loading, hold back the *newly added*
    // crumbs so the whole new tail (section[s] + leaf) animates in together once the label
    // arrives — matching the grouped fade-out on multi-level up-navigation. A safety timer
    // releases the hold so a route that never sets a leaf still shows its sections.
    const pendingLeaf = currentLeafKey !== null && !leafReady;
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in render via `holding` (drives the hold-prefix and the rendered trail); the rule only detects direct reads in the JSX return and misses reads through derived consts.
    const [holdReleasedKey, setHoldReleasedKey] = useState<string | null>(null);
    useEffect(() => {
        if (!pendingLeaf || !currentLeafKey) {
            return;
        }
        const key = currentLeafKey;
        const id = setTimeout(() => setHoldReleasedKey(key), LEAF_WAIT_MS);
        return () => clearTimeout(id);
    }, [pendingLeaf, currentLeafKey]);
    const holding = pendingLeaf && holdReleasedKey !== currentLeafKey;

    // Presence: keep the just-removed tail crumbs around (flagged leaving) so they can play
    // their CSS exit. The diff runs during render (adjust-state-during-render) so the exit
    // begins on the same paint as the URL change — an effect would be one render too late
    // and the crumb would pop out before reappearing to fade. Removals are always the tail
    // (the trail is append-only).
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in render via the keys diff below; must be state (not a ref) so its update re-renders and commits the exit before paint, and a ref read in render trips react-hooks/refs.
    const [prevCrumbs, setPrevCrumbs] = useState<Crumb[]>(structuralTarget);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in render via the presence diff and the flattened render list; the rule misses reads through derived consts.
    const [exitingCrumbs, setExitingCrumbs] = useState<Crumb[]>([]);

    // While holding, reveal only the crumbs already on screen (a leading prefix of the
    // structural trail); the new ones wait for the leaf and then enter as one group.
    const targetCrumbs = useMemo<Crumb[]>(() => {
        if (!holding) {
            return structuralTarget;
        }
        const shownKeys = new Set(prevCrumbs.map((crumb) => crumb.key));
        const held: Crumb[] = [];
        for (const crumb of structuralTarget) {
            if (!shownKeys.has(crumb.key)) {
                break;
            }
            held.push(crumb);
        }
        return held;
    }, [holding, structuralTarget, prevCrumbs]);

    const keysDiffer =
        prevCrumbs.length !== targetCrumbs.length ||
        prevCrumbs.some((crumb, index) => crumb.key !== targetCrumbs[index]?.key);
    if (keysDiffer) {
        // Compared by key (not reference) so the prevCrumbs update terminates — targetCrumbs
        // depends on prevCrumbs, so a reference check would loop.
        // With no motion a leaving crumb goes in this same render, unless a hold keeps it in place.
        setExitingCrumbs(
            targetCrumbs.length < prevCrumbs.length && (!noMotion || holding)
                ? prevCrumbs.slice(targetCrumbs.length)
                : []
        );
        setPrevCrumbs(targetCrumbs);
    }
    useEffect(() => {
        // While holding (waiting for a leaf to group a deep nav), keep the exiting crumb
        // mounted so its position — and therefore its leading chevron — stays put instead of
        // unmounting and re-entering when the held crumbs finally arrive.
        if (exitingCrumbs.length === 0 || holding) {
            return;
        }
        const timer = setTimeout(() => setExitingCrumbs([]), noMotion ? 0 : EXIT_MS);
        return () => clearTimeout(timer);
    }, [exitingCrumbs, noMotion, holding]);

    if (targetCrumbs.length === 0 && exitingCrumbs.length === 0) {
        return (
            <nav aria-label="breadcrumb" className={cn('hidden items-center gap-1.5 sm:flex', newDesign && 'gap-2')} />
        );
    }

    // Flatten into independently-keyed chevrons and labels rendered as ONE list so each
    // keeps its DOM node across a change (toggling `is-leaving` runs its CSS transition).
    // Chevrons are keyed by POSITION and only fade when that position is gone from the
    // *eventual* trail (`structuralTarget`) — so a same-depth label swap (Cases→Efforts) or
    // a replace-and-grow (Efforts → Cases > caseName) keeps the separator static and only
    // the labels animate. Labels are keyed by CONTENT, so they enter/exit on change.
    // Exiting crumbs are the removed tail, rendered after the current ones in place.
    const structuralDepth = structuralTarget.length;
    type FlatItem = { key: string; leaving: boolean } & (
        | { kind: 'chevron' }
        | { kind: 'label'; crumb: Crumb; isLast: boolean }
    );
    const items: FlatItem[] = [];
    targetCrumbs.forEach((crumb, index) => {
        if (index > 0) {
            items.push({ key: `chevron:${index}`, kind: 'chevron', leaving: index >= structuralDepth });
        }
        items.push({ key: crumb.key, kind: 'label', crumb, leaving: false, isLast: index === targetCrumbs.length - 1 });
    });
    exitingCrumbs.forEach((crumb, offset) => {
        const position = targetCrumbs.length + offset;
        items.push({ key: `chevron:${position}`, kind: 'chevron', leaving: position >= structuralDepth });
        items.push({ key: crumb.key, kind: 'label', crumb, leaving: true, isLast: false });
    });

    return (
        <nav aria-label="breadcrumb" className={cn('hidden items-center gap-1.5 sm:flex', newDesign && 'gap-2')}>
            {items.map((item) => {
                const wrapperClass = cn(
                    'inline-flex items-center',
                    !noMotion && 'breadcrumb-crumb',
                    item.leaving && 'is-leaving'
                );
                if (item.kind === 'chevron') {
                    return (
                        <span key={item.key} className={wrapperClass}>
                            {newDesign ? (
                                <span aria-hidden className="text-muted-foreground">
                                    /
                                </span>
                            ) : (
                                <ChevronRightIcon
                                    aria-hidden
                                    className="text-fg-secondary"
                                    style={{ fontSize: '0.75rem' }}
                                />
                            )}
                        </span>
                    );
                }
                // The active page (last crumb, not mid-exit) is the current location: render it
                // as non-navigable text with aria-current="page" so assistive tech announces it;
                // every other crumb stays a link. labelContent is shared so a last *section* crumb
                // routed to the span still renders its translated label (not a blank).
                const isCurrent = item.isLast && !item.leaving;
                const labelContent = item.crumb.labelId ? (
                    <FormattedMessage id={item.crumb.labelId} />
                ) : (
                    item.crumb.label
                );
                const colorClass = item.leaving || item.isLast ? 'text-fg' : 'text-fg-secondary';
                const newDesignClass =
                    newDesign &&
                    cn(
                        'text-body1',
                        item.leaving || item.isLast ? 'font-medium' : 'text-grey-600',
                        item.crumb.mono && 'text-meta font-mono'
                    );
                return (
                    <span key={item.key} className={wrapperClass}>
                        {item.crumb.to && !isCurrent ? (
                            <Link
                                to={item.crumb.to}
                                className={cn(
                                    'text-xs leading-5 no-underline transition-colors',
                                    colorClass,
                                    newDesignClass
                                )}
                            >
                                {labelContent}
                            </Link>
                        ) : (
                            <span
                                aria-current={isCurrent ? 'page' : undefined}
                                className={cn('text-xs leading-5 transition-colors', colorClass, newDesignClass)}
                            >
                                {labelContent}
                            </span>
                        )}
                    </span>
                );
            })}
        </nav>
    );
};

export default Breadcrumbs;
